import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import BackPage from "../../components/button/BackPage";
import RegularContainer from "../../components/container/RegularContainer";
import InnerTabs from "../../components/tab/InnerTabs";
import { useGetKnowledgeQuery } from "../../features/knowledges/services";
import { useGetProjectQuery } from "../../features/projects/services";
import { ProjectVersion } from "../../features/projects/types";
import { ILinkTabProps } from "../../types/interfaces";

const allTabRoutes: ILinkTabProps[] = [
    { labelKey: "KNOWLEDGE.Records_List", href: "knowledge-records" },
    { labelKey: "KNOWLEDGE.Smart_Generator", href: "smart-generator" },
    { labelKey: "KNOWLEDGE.Manual_Generator", href: "manual-generator" },
    { labelKey: "KNOWLEDGE.Record_Recycle_Bin", href: "knowledge-records-recycle-bin" },
    { labelKey: "KNOWLEDGE.Knowledge_Setting", href: "knowledge-settings" },
];

const externalKnowledgeTabRoutes: ILinkTabProps[] = [
    { labelKey: "KNOWLEDGE.Document_Management", href: "document-management" },
    { labelKey: "KNOWLEDGE.Records_List", href: "knowledge-records" },
    { labelKey: "KNOWLEDGE.Manual_Generator", href: "manual-generator" },
    { labelKey: "KNOWLEDGE.Knowledge_Setting", href: "knowledge-settings" },
];

const RecordsTabs = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const location = useLocation();
    const { state } = location;
    const { projectId, knowledgeId } = useParams();

    const [currentTab, setCurrentTab] = useState(0);
    const { data: knowledgeInfo } = useGetKnowledgeQuery(
        {
            id: knowledgeId as string,
        },
        { skip: !knowledgeId }
    );
    const { data: projectInfo } = useGetProjectQuery(
        {
            id: projectId as string,
        },
        { skip: !projectId }
    );

    const tabRoutes = useMemo(() => {
        const isV2Project = projectInfo?.data?.version === ProjectVersion.V2;
        return isV2Project ? externalKnowledgeTabRoutes : allTabRoutes;
    }, [projectInfo?.data?.version]);

    const knowledgeName = knowledgeInfo?.data?.title;
    const knowledgeLayoutTitle = `${t("RECORD.Manage_Record")} : ${knowledgeName}`;
    const handleChange = (newValue: number) => {
        setCurrentTab(newValue);
    };

    const pathnameSplit = location?.pathname?.split("/");
    const currentRoute = pathnameSplit[pathnameSplit?.length - 1] || "";

    const findIdx = tabRoutes.findIndex((r) => {
        return r.href === currentRoute;
    });
    const notFromHomePage = state?.split("/").length > 1;
    const backUrl = notFromHomePage ? `/projects/${projectId}/knowledge/inuse-knowledge` : `/projects/${projectId}`;
    useEffect(() => {
        if (findIdx !== -1) {
            setCurrentTab(findIdx);
            return;
        }
        setCurrentTab(0);
    }, [findIdx, currentRoute]);

    return (
        <>
            <BackPage path={backUrl} />
            <RegularContainer sx={{ pb: 0 }}>
                <RegularContainer.Heading title={knowledgeLayoutTitle} />
                <RegularContainer.Content>
                    <InnerTabs
                        currentTab={currentTab}
                        handleChange={handleChange}
                        tabRoutes={tabRoutes}
                        tabsOnClick={(tabRoute) => {
                            navigate(`/projects/${projectId}/knowledge/${knowledgeId}/${tabRoute.href}`, {
                                state: projectId,
                            });
                        }}
                    />
                </RegularContainer.Content>
            </RegularContainer>
        </>
    );
};

export default RecordsTabs;
